import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import notifications from "../../../../common/Components/Notifications/Notification";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { correctCashEntry, getCashEntry, requestCashChange } from "../../../../services/cashBookApi";
import { T, useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard from "../CashbookGuard";
import { fmtDateTime, num, refusalText } from "../money";
import type { CashEntryDetail, ChangeReasonCode, RecordType, StoryItem, SupervisorState } from "../types";
import Keypad from "../ui/Keypad";
import { STATE_WORD } from "./SupervisorCashHome";
import "../cashbook.scss";

/** Reason chips for a change (V1_SPEC D15, 11.4 A10). Free text only for "Something else". */
export const REASONS: { code: ChangeReasonCode; label: string }[] = [
  { code: "WRONG_AMOUNT", label: "Wrote wrong" },
  { code: "NOT_PAID_FULL", label: "Didn't pay in full" },
  { code: "DOUBLE", label: "Written twice" },
  { code: "OTHER_PERSON", label: "Was someone else's" },
  { code: "OTHER", label: "Something else" },
];

/** Can the change be sent? A reason chip, a typed amount, and words when the reason is "Something else". */
export const changeReady = (amount: string, code: ChangeReasonCode | null, text: string): boolean =>
  amount !== "" && !!code && (code !== "OTHER" || text.trim().length > 0);

/** What the supervisor is told after asking: the old amount stays, and how his cash moves on a yes (D17). */
export const afterChangeLine = (oldAmount: string, newAmount: string, t: T): string => {
  const diff = num(oldAmount) - num(newAmount);
  if (diff > 0) return t("{{old}} counts until the owner says yes. Then your cash in hand goes up by {{diff}}.", { old: rupees(oldAmount), diff: rupees(diff) });
  if (diff < 0) return t("{{old}} counts until the owner says yes. Then your cash in hand goes down by {{diff}}.", { old: rupees(oldAmount), diff: rupees(-diff) });
  return t("{{old}} counts until the owner says yes.", { old: rupees(oldAmount) });
};

/** The record's story in plain words, without audit terms (UX_COPY: supervisor screens). */
export const storyLine = (s: StoryItem, t: T): string | null => {
  const before = s.before !== null ? rupees(s.before) : "";
  const after = s.after !== null ? rupees(s.after) : "";
  switch (s.action) {
    case "RECORDED":
    case "ROW_ENTERED":
      return s.source === "PHOTO" ? t("{{amount}} from the photo", { amount: after }) : t("{{amount}} written by {{name}}", { amount: after, name: s.actor?.name || "" });
    case "CORRECTED":
      return t("Changed {{before}} → {{after}}", { before, after });
    case "VERIFIED":
    case "VERIFY_OVERRIDE":
      return t("Checked by {{name}}", { name: s.actor?.name || "" });
    case "CHANGE_PROPOSED":
      return s.after === null ? t("Change asked: remove this") : t("Change asked: {{before}} → {{after}}", { before, after });
    case "CHANGE_APPROVED":
      return s.after === null ? t("Removed") : t("Owner said yes: {{after}}", { after });
    case "CHANGE_REJECTED":
      return t("Owner kept {{before}}", { before: before || after });
    case "VOIDED":
      return t("Removed");
    case "SHEET_PRINTED":
    case "SHEET_REPRINTED":
      return t("Sheet printed");
    case "PHOTO_UPLOADED":
      return t("Photo sent");
    default:
      return null;
  }
};

const stateOf = (d: CashEntryDetail): SupervisorState => {
  const rec: any = d.row || d.entry;
  if (d.entry && d.entry.status !== "ACTIVE") return "VOID";
  if (rec?.pendingChange) return "CHANGE_PENDING";
  return rec?.verifyState === "VERIFIED" ? "CHECKED" : "SENT";
};

const VALID: RecordType[] = ["CASH_ROW", "PAY_ENTRY", "CASH_ENTRY"];

/**
 * One cash book record for the supervisor (V1_SPEC 11.4 A9-A11): what it is, whether it was checked,
 * and the way to fix it — "सुधारें" before the check, "बदलाव माँगें" after (the old amount stays until the
 * owner says yes). Its story is always shown.
 */
const EntryScreen = () => {
  const { t } = useKT();
  const { type, id } = useParams<{ type: string; id: string }>();
  const recordType = (VALID.includes(type as RecordType) ? type : "CASH_ENTRY") as RecordType;
  const status = useCashbookStatus();
  const [d, setD] = useState<CashEntryDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<null | "correct" | "change">(null);
  const [amount, setAmount] = useState("");
  const [code, setCode] = useState<ChangeReasonCode | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asked, setAsked] = useState<{ old: string; next: string } | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await getCashEntry(recordType, id);
      if (res?.success && res.data) setD(res.data);
      else setLoadError(refusalText(res, t));
    } catch {
      setLoadError(t("Couldn't load. Check the network."));
    }
  }, [recordType, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const header = <PageHeader title={t("Cash")} back={kp.cashBook()} backLabel={t("Back")} />;
  if (loadError && !d)
    return (
      <div className="cb">
        {header}
        <div className="cb-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  if (!d)
    return (
      <div className="cb" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton is-big" />
        <div className="cb-skeleton" />
      </div>
    );

  const row = d.row;
  const entry = d.entry;
  const current = (row ? row.paid : entry?.amount) ?? "0";
  const who = row ? row.name : entry?.kind === "EXPENSE" ? [entry.headName, entry.paidTo].filter(Boolean).join(" · ") : entry?.personName || "";
  const state = stateOf(d);
  const version = (row || entry)?.version ?? 0;
  const pending = (row || entry)?.pendingChange || null;
  // CashRowView has no sheet number; the story carries it.
  const sheetNo = d.story.find((s) => s.sheetNo)?.sheetNo || null;

  const open = (m: "correct" | "change") => {
    setMode(m);
    setAmount("");
    setCode(null);
    setText("");
    setError(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res =
        mode === "correct"
          ? await correctCashEntry(recordType, id, {
              version,
              amount: row ? null : amount,
              paid: row ? amount : null,
              ledgerHeadId: null,
              paidTo: null,
              note: null,
              void: false,
              reason: null,
            })
          : await requestCashChange(recordType, id, {
              version,
              // A pay or cash entry that should not exist at all is a removal; a sheet row is "paid 0".
              newAmount: !row && num(amount) === 0 ? null : amount,
              voidEntry: !row && num(amount) === 0,
              reasonCode: code as ChangeReasonCode,
              reason: code === "OTHER" ? text.trim() : null,
            });
      if (!res?.success || !res.data) {
        setError(refusalText(res, t));
        if (res?.code === "CHANGED_MEANWHILE" || res?.code === "USE_CHANGE_REQUEST" || res?.code === "CHANGE_PENDING") load();
        return;
      }
      if (mode === "change") setAsked({ old: current, next: amount });
      else notifications({ title: t("Corrected ✓"), message: "", type: "success" });
      setD(res.data);
      setMode(null);
      status.refresh();
    } catch {
      setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBusy(false);
    }
  };

  const story = d.story.map((s) => ({ s, line: storyLine(s, t) })).filter((x) => x.line);

  return (
    <div className="cb">
      {header}
      <div className="cb-big">
        <div className="cb-big__value" aria-live="polite">
          {rupees(current)}
        </div>
        <div className="cb-big__sum">
          {who}
          {row?.code ? ` · ${row.code}` : ""}
          {" · "}
          <span className={`cb-state ${state === "CHECKED" ? "is-ok" : "is-sent"}`}>{t(STATE_WORD[state])}</span>
        </div>
        {sheetNo ? <div className="cb-muted">{t("From {{sheetNo}}", { sheetNo })}</div> : null}
      </div>

      {asked ? (
        <div className="cb-card is-ok" role="status">
          <p>{afterChangeLine(asked.old, asked.next, t)}</p>
        </div>
      ) : null}

      {pending && !asked ? (
        <div className="cb-card is-warn">
          <p>
            {t("Change asked")}: {pending.voidEntry || pending.newAmount === null ? t("Change asked: remove this") : `${rupees(current)} → ${rupees(pending.newAmount)}`}
          </p>
          <p>{afterChangeLine(current, pending.newAmount ?? "0", t)}</p>
        </div>
      ) : null}

      {!mode && d.actions.correct ? (
        <>
          <button type="button" className="cb-btn is-primary" onClick={() => open("correct")}>
            {t("Correct")}
          </button>
          <p className="cb-muted" style={{ marginTop: 8 }}>
            {t("Every correction stays in the record.")}
          </p>
        </>
      ) : null}
      {!mode && d.actions.requestChange && !pending ? (
        <button type="button" className="cb-btn is-primary" onClick={() => open("change")}>
          {t("Request change")}
        </button>
      ) : null}

      {mode ? (
        <div className="cb-card">
          <Keypad value={amount} onChange={setAmount} label={t("Correct amount")} id="cb-entry-amount" />
          {mode === "change" ? (
            <>
              <div className="cb-field__label" id="cb-why" style={{ marginTop: 13 }}>
                {t("Why?")}
              </div>
              <div className="cb-chips" role="group" aria-labelledby="cb-why">
                {REASONS.map((r) => (
                  <button key={r.code} type="button" className={`cb-chip${code === r.code ? " is-on" : ""}`} aria-pressed={code === r.code} onClick={() => setCode(r.code)}>
                    {t(r.label)}
                  </button>
                ))}
              </div>
              {code === "OTHER" ? (
                <label className="cb-field">
                  <span className="cb-field__label">{t("Say what happened")}</span>
                  <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} autoFocus />
                </label>
              ) : null}
            </>
          ) : (
            <p className="cb-muted">{t("Every correction stays in the record.")}</p>
          )}
          {error ? (
            <div className="cb-error" role="alert">
              <span>{error}</span>
            </div>
          ) : null}
          <button
            type="button"
            className="cb-btn is-primary"
            disabled={busy || (mode === "change" ? !changeReady(amount, code, text) : amount === "")}
            onClick={submit}
          >
            {busy ? t("Saving…") : mode === "change" ? t("Send change") : t("Correct")}
          </button>
          <button type="button" className="cb-link" onClick={() => setMode(null)}>
            {t("Back")}
          </button>
        </div>
      ) : null}

      {story.length ? (
        <>
          <h3 className="cb-section">{t("What happened")}</h3>
          <ul className="cb-list">
            {story.map(({ s, line }) => (
              <li key={s.seq} className="cb-item">
                <span className="cb-item__main">
                  {line}
                  {s.reason ? <span className="cb-item__sub">“{s.reason}”</span> : null}
                </span>
                <span className="cb-item__sub">{fmtDateTime(s.at)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
};

const EntryDetail = () => (
  <CashbookGuard>
    <EntryScreen />
  </CashbookGuard>
);

export default EntryDetail;
